import _ from "lodash";
import React, { useEffect, useRef, useMemo, useCallback } from "react";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import classes from "./TheatreMode.scss";
import MediaPlaceholder from "UIComponents/MediaPlaceholder";
import I18nHOC from "UIComponents/I18nHOC";
import FileViewer from "UIComponents/FileViewer";
import MicrosoftFileViewer from "UIComponents/FileViewer/MicrosoftFileViewer";
import ImageViewer from "UIComponents/ImageViewer";
import {
  GetTheatreModeType,
  WORKBOOK_MODE_TYPES,
  PDFTRON_VIEWER_MODE_TYPES,
  WORKBOOK_IFRAME_ID_SELECTORS,
  getThumbnailIcon,
  formatBytes,
} from "Utils";
import Workbook from "AppComponents/Workbook";
import { PdfTronViewerV3 } from "AppComponents/PdfTronViewer";
import LTIComponent from "AppComponents/LTIComponent";
import ACLStore from "lib/aclStore";
import { getStudentAssignmentMicrosoftLink } from "ClassRoom/modules/ClassRoomModule";
import { ArrowOutOutlined } from "@toddle-edu/ds-icons";
import { Button } from "@toddle-edu/ds-web";
import { getAttachmentType } from "Utils/GetTheatreModeType";
import { openLinkInNewTab } from "modules/Services";
import { APPLE_OFFICE_SUPPORTED_MIMETYPES } from "Constants";

const styles = {
  mediaPlaceHolderContainerStyle: {
    alignItems: "center",
    width: "100%",
  },
  noteContainerStyle: {
    height: "unset",
  },
  noteContainerWithLimitStyles: {
    height: "unset",
    maxHeight: "unset",
    WebkitBoxOrient: "unset",
  },
};

const WORKBOOK_DISABLED_ITEMS = ["CANCEL_BTN"];

const ViewerComponent = React.memo(props => {
  const {
    theatreModeRef,
    currentMediaData,
    onChangeActiveImage,
    setShowGiveFeedbackModal,
    shouldShowGiveFeedbackButton,
    imageOrientation,
    updateImageOrientation,
    onWorkbookUpdateSuccessfully,
    parentRef,
    iframeIdSelector,
    onPdftronUpdateSuccessfullly,
    isTextFocus,
    userInfo,
    t,
    onDiscardDirectly,
    studentDetails,
    invokeWorkbookCustomLoader,
    onPdfTronSwitchToEditMode,
    preLoadPdfTron,
    mediaHeaderConStyle = {},
    mediaPlaceWrapperStyle = {},
    fullScreenButtonStatus,
    toggleShowTheatreModeV2,
    showSaveButton,
    xfdfString,
    setPdftronInstance,
    fileViewerMode,
    pdftronViewerMode,
    studentSubmissionId,
    getStudentAssignmentMicrosoftLink,
    openLinkInNewTab,
    playbackButtonPositionStyle,
  } = props;

  const mediaDataAttachments = _.get(currentMediaData, "attachments");
  const mediaDataAttachment = _.first(mediaDataAttachments) || currentMediaData;

  const theatreModeType = GetTheatreModeType({ media: mediaDataAttachment });
  if (
    ACLStore.can("FeatureFlag:WorkbookPreloadWeb") &&
    theatreModeRef &&
    theatreModeRef.current &&
    _.isEqual(theatreModeType, "WORKBOOKVIEWER") &&
    iframeIdSelector &&
    _.isEqual(iframeIdSelector, WORKBOOK_IFRAME_ID_SELECTORS["CLASSROOM"])
  ) {
    theatreModeRef.current.style.height = "0%";
  }
  const isGoogleDocs =
    _.isEqual(theatreModeType, "FILEVIEWER") &&
    _.get(mediaDataAttachment, "type") == "LINK" &&
    _.startsWith(_.get(mediaDataAttachment, "url"), "https://docs.google.com/");

  const mediaDataAttachmentParentType = _.get(
    mediaDataAttachment,
    "parentType",
    ""
  );

  const isPlaybackSupportedGoogleDocs =
    ACLStore.can("FeatureFlag:ProjectGooglePlaybackFixs") &&
    !_.isEmpty(mediaDataAttachmentParentType)
      ? _.includes(
          ["ATTACHMENT_GROUP", "PROJECT_SUBMISSION_ITEM_RESPONSE"],
          mediaDataAttachmentParentType
        )
      : true;

  const showGoogleDocsPlaybackButton =
    isGoogleDocs &&
    _.includes(_.get(mediaDataAttachment, "mimeType", ""), [
      "application/vnd.google-apps.document",
    ]) &&
    ACLStore.can("FeatureFlag:EnableGoogleDocsPlayback") &&
    isPlaybackSupportedGoogleDocs;

  const onClickToOpenNewTab = () => {
    const isMicrosoftTemplate =
      _.get(mediaDataAttachment, "metadata.serviceType", "") == "MICROSOFT" &&
      _.has(mediaDataAttachment, "metadata.permission");
    const attachmentUrl = isMicrosoftTemplate
      ? !_.isNil(_.get(mediaDataAttachment, "metadata.viewUrl"))
        ? _.get(mediaDataAttachment, "metadata.viewUrl")
        : _.get(props, "userInfo.user_type", "") === "staff"
        ? _.get(mediaDataAttachment, "url")
        : undefined
      : _.get(mediaDataAttachment, "url");
    if (attachmentUrl) openLinkInNewTab({ url: attachmentUrl });
  };

  const filterDataExtraParams = useMemo(
    () => ({
      attachmentId: _.get(mediaDataAttachment, "metadata.attachmentId"),
      workAttachmentId: _.get(mediaDataAttachment, "id"),
    }),
    [mediaDataAttachment]
  );

  const memoizedAttachmentsArray = useMemo(() => [mediaDataAttachment], [
    mediaDataAttachment,
  ]);

  switch (theatreModeType) {
    case "MIRCOSOFT_URL": {
      const isMicrosoftTemplate =
        _.has(mediaDataAttachment, "metadata.permission") &&
        !_.isEmpty(_.get(currentMediaData, "studentTemplate", {}));

      const attachmentId = _.get(mediaDataAttachment, "id");

      if (
        isMicrosoftTemplate &&
        !_.isEmpty(studentSubmissionId) &&
        !_.isEmpty(attachmentId) &&
        _.isEqual(_.get(props, "userInfo.user_type", ""), "staff") &&
        ACLStore.can("FeatureFlag:EnableMicrosoftDocsEmbedding")
      ) {
        return (
          <MicrosoftFileViewer
            studentSubmissionId={studentSubmissionId}
            attachmentId={attachmentId}
          />
        );
      }
      const onClickMedia = async () => {
        const attachmentId = _.get(mediaDataAttachment, ["id"]);
        const url =
          attachmentId && studentSubmissionId
            ? await getStudentAssignmentMicrosoftLink({
                attachmentId,
                submissionId: studentSubmissionId,
              })
            : _.get(mediaDataAttachment, "url", "");
        // eslint-disable-next-line no-restricted-properties
        window.open(url, "_blank", "noopener noreferrer");
      };
      const iconSvg = getThumbnailIcon({
        type: mediaDataAttachment?.type,
        mimeType: mediaDataAttachment?.mimeType,
      });

      const fileSize = mediaDataAttachment?.metadata?.size || 0;

      return (
        <React.Fragment>
          <div className={classes.mediaHeaderCon} style={mediaHeaderConStyle}>
            <div className={classes.headerLeftCon}>
              {iconSvg && iconSvg}

              <div className={classes.mediaNameTextRevamp}>
                {mediaDataAttachment?.name ||
                  mediaDataAttachment?.title ||
                  t("common:untitled")}
              </div>
              <div className={classes.dot} />

              <div className={classes.mediaType}>
                {mediaDataAttachment?.type}
                {fileSize > 0 &&
                  ` (${formatBytes({ bytes: fileSize, decimals: 1 })})`}
              </div>
            </div>
          </div>
          <div
            className={classes.mediaPlaceWrapper}
            style={mediaPlaceWrapperStyle}
          >
            <MediaPlaceholder
              attachments={[mediaDataAttachment]}
              imageMaxHeight={575}
              imageMaxWidth={920}
              showVideoPlayer={true}
              showFullscreenImage={true}
              containerStyle={styles.mediaPlaceHolderContainerStyle}
              noteContainerStyle={styles.noteContainerStyle}
              noteContainerWithLimitStyles={styles.noteContainerWithLimitStyles}
              showLinkInNewTab={false}
              onClickMedia={onClickMedia}
            />
          </div>
        </React.Fragment>
      );
    }
    case "WORKBOOKVIEWER":
      return (
        <Workbook
          attachment={mediaDataAttachment}
          workbookMode={WORKBOOK_MODE_TYPES["WORKBOOK_FEEDBACK_VIEW"]}
          isFullScreen={false}
          onUpdateSuccessfully={onWorkbookUpdateSuccessfully}
          disableArr={WORKBOOK_DISABLED_ITEMS}
          setShowGiveFeedbackModal={setShowGiveFeedbackModal}
          shouldShowGiveFeedbackButton={shouldShowGiveFeedbackButton}
          parentRef={parentRef}
          iframeIdSelector={iframeIdSelector}
          invokeWorkbookCustomLoader={invokeWorkbookCustomLoader}
        />
      );

    case "IMAGEVIEWER":
      return (
        <ImageViewer
          key={mediaDataAttachment.id}
          attachment={mediaDataAttachment}
          attachmentsArray={memoizedAttachmentsArray}
          showInsideParent={true}
          allowDrag={true}
          onChangeActiveImage={onChangeActiveImage}
          showCloseButton={false}
          shouldShowGiveFeedbackButton={shouldShowGiveFeedbackButton}
          setShowGiveFeedbackModal={setShowGiveFeedbackModal}
          imageOrientation={imageOrientation}
          updateImageOrientation={updateImageOrientation}
          isDisableKeyboard={isTextFocus}
        />
      );

    case "FILEVIEWER":
      return (
        <FileViewer
          file={mediaDataAttachment}
          key={mediaDataAttachment.id}
          useIframePartialLoader={isGoogleDocs}
          mode={fileViewerMode}
          showGoogleDocsPlaybackButton={showGoogleDocsPlaybackButton}
          playbackButtonPositionStyle={playbackButtonPositionStyle}
        />
      );

    case "PDFTRONVIEWER":
      if (preLoadPdfTron) return null;

      return (
        <PdfTronViewerV3
          xfdfString={xfdfString}
          file={mediaDataAttachment}
          key={mediaDataAttachment.id}
          shouldShowGiveFeedbackButton={shouldShowGiveFeedbackButton}
          onPdfTronSwitchToEditMode={onPdfTronSwitchToEditMode}
          studentDetails={studentDetails}
          pdftronViewerMode={pdftronViewerMode}
          annotationUserInfo={userInfo}
          onDiscardDirectly={onDiscardDirectly}
          onUpdateSuccessfully={onPdftronUpdateSuccessfullly}
          fullScreenButtonStatus={fullScreenButtonStatus}
          toggleShowTheatreModeV2={toggleShowTheatreModeV2}
          showSaveButton={showSaveButton}
          setPdftronInstance={setPdftronInstance}
        />
      );
    case "MEDIAPLACEHOLDER": {
      const iconSvg = getThumbnailIcon({
        type: mediaDataAttachment?.type,
        mimeType: mediaDataAttachment?.mimeType,
      });

      const fileSize = mediaDataAttachment?.metadata?.size || 0;

      let showOpenInNewTabButton = false;
      const attachmentTypeByMimeType = getAttachmentType(mediaDataAttachment);
      const isAppleDocumentFile = _.includes(
        APPLE_OFFICE_SUPPORTED_MIMETYPES,
        _.get(mediaDataAttachment, "mimeType")
      );

      // If type is LINK and attachment is not previewable OR type is FILE and attachment is apple document file
      if (
        (_.get(mediaDataAttachment, "type", "") == "LINK" &&
          _.includes(
            ["PDF", "IMAGE", "VIDEO", "AUDIO", "FILE"],
            attachmentTypeByMimeType
          )) ||
        _.get(mediaDataAttachment, "type", "") == "FILE" ||
        isAppleDocumentFile
      ) {
        showOpenInNewTabButton = true;
      }

      return (
        <React.Fragment>
          <div className={classes.mediaHeaderCon} style={mediaHeaderConStyle}>
            <div className={classes.headerLeftCon}>
              {iconSvg && iconSvg}

              <div className={classes.mediaNameTextRevamp}>
                {mediaDataAttachment?.name ||
                  mediaDataAttachment?.title ||
                  t("common:untitled")}
              </div>
              <div className={classes.dot} />

              <div className={classes.mediaType}>
                <span className={classes.flex}>
                  {mediaDataAttachment?.type}
                </span>
                {fileSize > 0 ? (
                  <span className={classes.flex}>
                    {`(${formatBytes({ bytes: fileSize, decimals: 1 })})`}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
          {showOpenInNewTabButton ? (
            <div className={classes.openNewTabViewContainer}>
              <div className={classes.iconCardMediaPlaceholderContainer}>
                <MediaPlaceholder
                  attachments={[mediaDataAttachment]}
                  imageMaxHeight={176}
                  imageMaxWidth={176}
                  sourceWidth={176}
                  sourceHeight={176}
                  containerStyle={styles.mediaPlaceHolderContainerStyle}
                  itemStyles={{
                    width: "176px",
                    height: "176px",
                    borderRadius: "8px",
                  }}
                  showIconCardsWithExtension={true}
                  showMiddleContainerIcons={false}
                  showVideoPlayer={false}
                  onClickMedia={onClickToOpenNewTab}
                  scalableRatio={1}
                />
              </div>
              <Button
                variant="primary"
                size="small"
                icon={<ArrowOutOutlined />}
                style={{ marginTop: "16px" }}
                onClick={onClickToOpenNewTab}
              >
                {t("common:open_in_new_tab")}
              </Button>
            </div>
          ) : (
            <div
              className={classes.mediaPlaceWrapper}
              style={mediaPlaceWrapperStyle}
            >
              <MediaPlaceholder
                attachments={[mediaDataAttachment]}
                imageMaxHeight={575}
                imageMaxWidth={920}
                showVideoPlayer={true}
                showFullscreenImage={true}
                containerStyle={styles.mediaPlaceHolderContainerStyle}
                noteContainerStyle={styles.noteContainerStyle}
                noteContainerWithLimitStyles={
                  styles.noteContainerWithLimitStyles
                }
                showLinkInNewTab={true}
              />
            </div>
          )}
        </React.Fragment>
      );
    }
    case "LTI_TOOL": {
      return (
        <LTIComponent
          filterDataExtraParams={filterDataExtraParams}
          courseId={_.get(mediaDataAttachment, "metadata.courseId")}
          serviceName={_.get(mediaDataAttachment, "metadata.service")}
          resourceLinkId={_.get(
            mediaDataAttachment,
            "metadata.resource_link_id"
          )}
          showModal={false}
          showEmptyState={_.get(
            mediaDataAttachment,
            "metadata.disableTeacherReportView",
            false
          )}
          studentName={_.get(studentDetails, "firstName", "")}
        />
      );
    }
  }
});

ViewerComponent.displayName = "ViewerComponent";

const PdfTronView = ({
  currentMediaData,
  shouldShowGiveFeedbackButton,
  parentRef,
  iframeIdSelector,
  onPdftronUpdateSuccessfullly,
  userInfo,
  onDiscardDirectly,
  studentDetails,
  showTheatreView,
  onPdfTronSwitchToEditMode,
  invokeWorkbookCustomLoader,
  fullScreenButtonStatus,
  toggleShowTheatreModeV2,
  showSaveButton,
  xfdfString,
  setPdftronInstance,
  pdftronViewerMode,
}) => {
  const mediaDataAttachment =
    _.first(_.get(currentMediaData, "attachments")) || currentMediaData;

  const isPdfAttachment = useMemo(() => {
    const mediaDataAttachment =
      _.first(_.get(currentMediaData, "attachments")) || currentMediaData;
    const theatreModeType = GetTheatreModeType({ media: mediaDataAttachment });
    const isPdf =
      showTheatreView && _.isEqual(theatreModeType, "PDFTRONVIEWER");

    return isPdf;
  }, [showTheatreView, currentMediaData]);

  const useCustomLoader =
    isPdfAttachment && invokeWorkbookCustomLoader ? true : false;

  if (_.isEmpty(mediaDataAttachment)) {
    return null;
  }

  return (
    <PdfTronViewerV3
      hidePdfTronContainer={!isPdfAttachment}
      file={mediaDataAttachment}
      shouldShowGiveFeedbackButton={shouldShowGiveFeedbackButton}
      onPdfTronSwitchToEditMode={onPdfTronSwitchToEditMode}
      studentDetails={studentDetails}
      pdftronViewerMode={pdftronViewerMode}
      annotationUserInfo={userInfo}
      onDiscardDirectly={onDiscardDirectly}
      onUpdateSuccessfully={onPdftronUpdateSuccessfullly}
      useCustomLoader={useCustomLoader}
      fullScreenButtonStatus={fullScreenButtonStatus}
      toggleShowTheatreModeV2={toggleShowTheatreModeV2}
      showSaveButton={showSaveButton}
      xfdfString={xfdfString}
      setPdftronInstance={setPdftronInstance}
    />
  );
};

const TheatreMode = React.memo(props => {
  const theatreModeRef = useRef(null);
  const {
    showTheatreView,
    preLoadPdfTron,
    onClickLeft,
    onClickRight,
    isWorkbookEnabled,
    isPdftronEnabled,
  } = props;

  const handleMessageFromIframe = useCallback(
    event => {
      const message = _.get(event, "data", "");

      if (isWorkbookEnabled || isPdftronEnabled) {
        return;
      }

      switch (message) {
        case "CAN_NAVIGATE_PREVIOUS": {
          onClickLeft?.({ isShortcut: true });
          break;
        }
        case "CAN_NAVIGATE_NEXT": {
          onClickRight?.({ isShortcut: true });
          break;
        }
      }
    },
    [onClickLeft, onClickRight, isWorkbookEnabled, isPdftronEnabled]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessageFromIframe);
    return () => {
      window.removeEventListener("message", handleMessageFromIframe);
    };
  }, [handleMessageFromIframe]);

  return (
    <div
      className={classes.container}
      id={WORKBOOK_IFRAME_ID_SELECTORS["THEATRE_MODE"]}
      ref={theatreModeRef}
    >
      {preLoadPdfTron ? (
        <>
          {showTheatreView && (
            <ViewerComponent {...props} theatreModeRef={theatreModeRef} />
          )}
          <PdfTronView {...props} theatreModeRef={theatreModeRef} />
        </>
      ) : (
        <ViewerComponent {...props} theatreModeRef={theatreModeRef} />
      )}
    </div>
  );
});

TheatreMode.displayName = "TheatreMode";

TheatreMode.defaultProps = {
  showTheatreView: true,
  preLoadPdfTron: false,
  pdftronViewerMode: PDFTRON_VIEWER_MODE_TYPES["TEACHER_EDITOR"],
};

const mapStateToProps = state => {
  const userInfo = _.pick(_.get(state, "login.userInfo", {}), [
    "id",
    "first_name",
    "last_name",
    "email",
    "userEntityType",
    "user_type",
  ]);

  return {
    userInfo,
  };
};

const mapActionCreators = {
  getStudentAssignmentMicrosoftLink,
  openLinkInNewTab,
};

export default compose(
  I18nHOC({ resource: ["common", "journal"] }),
  connect(mapStateToProps, mapActionCreators)
)(TheatreMode);
